import { h } from "preact";
import { renderIsland } from "../../ui/renderIsland";
import { ScoreBoxes, StatusDisplay } from "./DirectoryStatus";

/** The `api.artwork.directoryEditRefresh` fields this table renders. */
export interface DirectoryEditData {
  score: number;
  alternateVersionCount: number;
  alternateArtworkId: number | null;
  artworkStage: string;
  hasCategory: boolean;
  /** Expanded-products opt-in still pending. No longer changes the status — see
   *  `StatusDisplay` — but the payload still carries it. */
  needsOptIn: boolean;
  megapixels: number | null;
  reuploadStatus: number | null;
}

/** PHP re-upload status labels, keyed by the legacy int. */
const REUPLOAD_LABELS: Record<number, { color: string; text: string }> = {
  1: { color: "orange", text: "Re-upload awaiting approval" },
  2: { color: "green", text: "Re-upload approved" },
  3: { color: "red", text: "Re-upload rejected" },
};

function Row({ header, children }: { header: string; children: preact.ComponentChildren }) {
  return (
    <tr>
      <td class="directory-table-header">{header}</td>
      <td class="directory-table-value">{children}</td>
    </tr>
  );
}

/**
 * The artwork detail table in the directory-edit modal.
 *
 * Replaces `buildDirectoryEditHtml()` in `features/directoryUpload.ts`, which
 * duplicated the card's score meter and status logic as strings — and had drifted
 * from it. Both now render through `DirectoryStatus.tsx` off the same inputs.
 */
export function DirectoryEditTable({ data }: { data: DirectoryEditData }) {
  const {
    score,
    alternateVersionCount,
    alternateArtworkId,
    artworkStage,
    megapixels,
    reuploadStatus,
  } = data;
  const reupload = reuploadStatus === null ? undefined : REUPLOAD_LABELS[reuploadStatus];

  return (
    <table class="directory-table" cellPadding="0" cellSpacing="0">
      {/* Only one of these two ever shows: an artwork is either a main with
          alternates, or itself an alternate. */}
      {alternateVersionCount ? (
        <Row header="Alternative versions">{String(alternateVersionCount)}</Row>
      ) : alternateArtworkId ? (
        <Row header="Main artwork">#{String(alternateArtworkId)}</Row>
      ) : null}

      <Row header="Optimisation score">
        <ScoreBoxes score={score} />
      </Row>

      <Row header="Status">
        <StatusDisplay stage={artworkStage} megapixels={megapixels} />
      </Row>

      {reupload && (
        <Row header="Re-upload">
          <span style={`color: ${reupload.color};`}>{reupload.text}</span>
        </Row>
      )}
    </table>
  );
}

/** Re-render the detail table for one artwork's edit modal. */
export function renderDirectoryEditTable(artworkId: string, data: DirectoryEditData): void {
  renderIsland(`directory-edit-${artworkId}`, h(DirectoryEditTable, { data }));
}
